// src/api/memberApi.ts
import {
  MemberAvatarProfile,
  MemberListResponse,
  MemberSerializer,
  MemberResponse,
  MemberUuidInfo,
  MemberUuidInfoResponse,
  TokenUserPayloadSerializer,
} from "@/types/member"
import { CommonResponse, CommonPageTResponse } from "@/types/common"
import { serverApi, getAuthHeaders, handleApiResponse } from "@core/api/core"
import {
  clearStoredAuthState,
  dispatchAuthStorageEvent,
  persistWebSessionAuth,
  refreshStoredWebSessionExpiry,
  toSessionMemberPayload,
  type WebSessionUserPayload,
} from "@core/utils/webSessionAuth"
import { recordSessionExit } from "@core/utils/sessionExitTracker"
import { clearEncounterHistoryInitialPageCache } from "@core/api/encounterHistoryApi"

// DeviceToken 사용 가능 여부 응답 타입 (camelCase 자동 변환됨)
export interface AvailabilityStatusResponse {
  isToken: boolean // 유효한 토큰 존재 여부
  isTrial: boolean // 체험판 모드 여부
  isAvailable: boolean // 서비스 사용 가능 여부
  remainDays: number // 체험판 남은 일수 (체험판이 아니면 0)
}

// 사용 가능 여부를 가져오는 함수
export const getAvailabilityStatus = async (): Promise<AvailabilityStatusResponse> => {
  try {
    const requestUrl = `/member/public/available/`
    const response = await serverApi.get(requestUrl)
    console.debug("getAvailabilityStatus response:", response)
    return handleApiResponse<AvailabilityStatusResponse>(response)
  } catch (error: any) {
    // 404, 503 등 URL이 존재하지 않는 경우 (구버전 서버) → 기본값 반환
    if (error.response?.status === 404 || error.response?.status === 503) {
      console.debug("getAvailabilityStatus: API 미지원 (구버전 서버) → 기본값 반환")
      return {
        isToken: true,
        isTrial: false,
        isAvailable: false,
        remainDays: 0,
      }
    }
    // 그 외 에러는 재throw
    throw error
  }
}

// 관리자 설치 여부를 가져오는 함수
export const getInstallationStatus = async (): Promise<boolean> => {
  // console.debug("서버에 설치된 차트 관리자 확인1")
  const requestUrl = `/member/public/installed/`
  const response = await serverApi.get<CommonResponse>(requestUrl)
  return handleApiResponse<boolean>(response)
}

// 관리자 등록 API 호출 함수
export const createSuperuser = async (superuserData: MemberSerializer): Promise<boolean> => {
  const requestUrl = `/member/create_superuser/`
  const response = await serverApi.post(requestUrl, superuserData)
  return handleApiResponse<boolean>(response)
}
// ──────────────────────────────────────────────────────────────────────────────

interface payloadType {
  memberId: string
  password: string
}

export interface LoginResult {
  token: string
  passwordChangeRequired: boolean
  user?: TokenUserPayloadSerializer
  authMode: "web-session"
}

interface WebSessionLoginResult {
  authenticated: boolean
  passwordChangeRequired: boolean
  expiresIn?: number
  csrfToken?: string
  user?: WebSessionUserPayload
}

interface WebSessionStatusResult {
  authenticated: boolean
  expiresIn?: number
  csrfToken?: string
  user?: WebSessionUserPayload
}

// 사용자 로그인 함수
// 성공 시 세션 사용자 정보를 저장하고 반환, 실패 시 에러 throw
export const memberLogin = async (payload: payloadType): Promise<LoginResult> => {
  const csrfRequestUrl = `/web/auth/csrf/`
  const csrfResponse = await serverApi.get<CommonResponse>(csrfRequestUrl, {
    withCredentials: true,
  })
  const csrfData = handleApiResponse<{ csrfToken?: string }>(csrfResponse)
  const csrfToken = csrfData.csrfToken || ""

  const requestUrl = `/web/auth/login/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload, {
    withCredentials: true,
    headers: csrfToken
      ? {
          "X-CSRFToken": csrfToken,
        }
      : undefined,
  })
  const responseData = handleApiResponse<WebSessionLoginResult>(response)
  const { passwordChangeRequired = false } = responseData

  // 비밀번호 변경이 필요한 경우 토큰이 없을 수 있음
  if (passwordChangeRequired) {
    clearEncounterHistoryInitialPageCache()
    clearStoredAuthState()
    return {
      token: "", // 빈 토큰
      passwordChangeRequired: true,
      authMode: "web-session",
    }
  }

  if (!responseData.authenticated || !responseData.user) {
    throw new Error("로그인 응답에 사용자 정보가 없습니다.")
  }

  clearEncounterHistoryInitialPageCache()
  persistWebSessionAuth({
    user: responseData.user,
    expiresIn: responseData.expiresIn,
    csrfToken: responseData.csrfToken || csrfToken,
  })

  const normalizedUser = toSessionMemberPayload(responseData.user, responseData.expiresIn)

  // storage 이벤트 트리거 (채팅창 자동 열기 위해)
  window.dispatchEvent(new Event("storage"))

  return {
    token: "",
    passwordChangeRequired: false,
    user: normalizedUser,
    authMode: "web-session",
  }
}

export const getWebSessionStatus = async (): Promise<WebSessionStatusResult> => {
  try {
    const response = await serverApi.get<CommonResponse>(`/web/auth/session/`, {
      withCredentials: true,
    })
    const result = handleApiResponse<WebSessionStatusResult>(response)

    if (typeof result.expiresIn === "number") {
      refreshStoredWebSessionExpiry(result.expiresIn)
    }

    return result
  } catch (error: any) {
    const statusCode = error?.response?.status

    if (statusCode === 401 || statusCode === 403) {
      recordSessionExit("token_not_valid_response", {
        apiName: "WebSessionStatus",
        triggerUrl: "/web/auth/session/",
        method: "get",
        statusCode,
        errorCode: error?.response?.data?.code,
        data: error?.response?.data,
        source: "getWebSessionStatus",
      })
      clearEncounterHistoryInitialPageCache()
      clearStoredAuthState()
      dispatchAuthStorageEvent()
    }

    throw error
  }
}

export const logoutMemberSession = async (): Promise<void> => {
  const csrfToken = localStorage.getItem("sigma:webCsrfToken") || ""

  // 서버 응답 대기 여부와 무관하게 즉시 세션을 비운다.
  clearEncounterHistoryInitialPageCache()
  clearStoredAuthState()
  dispatchAuthStorageEvent()

  try {
    await serverApi.post(
      `/web/auth/logout/`,
      {},
      {
        withCredentials: true,
        headers: csrfToken
          ? {
              "X-CSRFToken": csrfToken,
            }
          : undefined,
      }
    )
  } catch (error: any) {
    if (error?.response?.status !== 401) {
      console.warn("[logoutMemberSession] 세션 로그아웃 요청 실패", error)
    }
  }
}

export interface PersonalMemoResponse {
  personalMemo: string
}

export interface MemberAvatarResponse extends MemberAvatarProfile {}

export const getMyPersonalMemo = async (): Promise<PersonalMemoResponse> => {
  const response = await serverApi.get<CommonResponse>(`/web/member/personal-memo/`, {
    withCredentials: true,
  })
  return handleApiResponse<PersonalMemoResponse>(response)
}

export const updateMyPersonalMemo = async (personalMemo: string): Promise<PersonalMemoResponse> => {
  const response = await serverApi.patch<CommonResponse>(
    `/web/member/personal-memo/`,
    { personalMemo },
    {
      withCredentials: true,
    }
  )
  return handleApiResponse<PersonalMemoResponse>(response)
}

export const getMyAvatarProfile = async (): Promise<MemberAvatarResponse> => {
  const response = await serverApi.get<CommonResponse>(`/web/member/avatar/`, {
    withCredentials: true,
  })
  return handleApiResponse<MemberAvatarResponse>(response)
}

export const updateMyAvatarProfile = async (payload: MemberAvatarProfile): Promise<MemberAvatarResponse> => {
  const response = await serverApi.patch<CommonResponse>(`/web/member/avatar/`, payload, {
    withCredentials: true,
  })
  return handleApiResponse<MemberAvatarResponse>(response)
}

export interface MyAccountProfile {
  memberUuid: string
  memberGrant: string
  memberId: string
  email: string
  memberProfileName: string
  memberName: string
}

export type MyAccountUpdate = Pick<MyAccountProfile, "memberId" | "email" | "memberProfileName" | "memberName">

export const getMyAccount = async (): Promise<MyAccountProfile> => {
  const response = await serverApi.get<CommonResponse>("/web/member/account/", { withCredentials: true })
  return handleApiResponse<MyAccountProfile>(response)
}

export const updateMyAccount = async (payload: MyAccountUpdate): Promise<MyAccountProfile> => {
  const response = await serverApi.patch<CommonResponse>("/web/member/account/", payload, { withCredentials: true })
  return handleApiResponse<MyAccountProfile>(response)
}

export const changeMyPassword = async (payload: Omit<ChangePasswordRequest, "memberId">): Promise<void> => {
  const response = await serverApi.post<CommonResponse>("/web/member/account-password/", payload, { withCredentials: true })
  return handleApiResponse<void>(response)
}

// 의료진 정보 조회
export const getMemberInfo = async (memberUuid: string): Promise<MemberUuidInfo> => {
  const requestUrl = `/member/by-uuid/${memberUuid}/`
  const response = await serverApi.get<MemberUuidInfoResponse>(requestUrl)
  // console.log("getMemberInfo response:", response)
  return handleApiResponse<MemberUuidInfo>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 비밀번호 찾기
interface payloadForgetPasswordType {
  memberId: string
  email: string
}

export const postForgotPassword = async (payload: payloadForgetPasswordType): Promise<boolean> => {
  const requestUrl = `/member/forgot_password/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 신규계정 생성
export const createMemberSignup = async (payload: MemberSerializer): Promise<boolean> => {
  const requestUrl = `/member/signup/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 비밀번호 변경
export interface ChangePasswordRequest {
  memberId: string
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export const changePassword = async (payload: ChangePasswordRequest): Promise<boolean> => {
  const requestUrl = `/member/change_password/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// 비밀번호 확인
export interface VerifyPasswordRequest {
  current_password: string
}

export const verifyPassword = async (payload: VerifyPasswordRequest): Promise<boolean> => {
  const requestUrl = `/member/verify_password/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)

  // 서버가 {success, result} 객체를 반환하므로 내부 result(boolean)를 추출
  return handleApiResponse<{ success: boolean; result: boolean }>(response).result
}

// 공용 타입
export interface MemberStampResponse {
  stampImage: string | null // Base64 (data URL 포함 가능)
  mimeType: string | null
  updatedAt: string | null
}

export const normalizeMemberStampImage = (stampImage?: string | null, mimeType?: string | null) => {
  if (!stampImage) {
    return null
  }

  if (stampImage.startsWith("data:")) {
    return stampImage
  }

  return `data:${mimeType || "image/png"};base64,${stampImage}`
}

const buildMemberStampUrl = (memberUuid: string) => `/member/${memberUuid}/stamp/`

// === 본인 도장 ===
// GET /member/stamp/me/
export const getMyStampImage = async (): Promise<MemberStampResponse> => {
  const res = await serverApi.get("/member/stamp/me/")
  console.log("getMyStampImage response:", res)
  return handleApiResponse<MemberStampResponse>(res)
}

// POST /member/stamp/upload/
export const uploadMyStampImage = async (stampImageBase64: string, mimeType = "image/png"): Promise<MemberStampResponse> => {
  const res = await serverApi.post("/member/stamp/upload/", {
    stampImageBase64,
    mimeType,
  })
  return handleApiResponse<MemberStampResponse>(res)
}

// DELETE /member/stamp/delete/
export const deleteMyStampImage = async (): Promise<{ success: boolean }> => {
  const res = await serverApi.delete("/member/stamp/delete/")
  handleApiResponse(res)
  return { success: true }
}

// === 관리자: 특정 멤버 도장 ===
export const getMemberStampImage = async (memberUuid: string): Promise<MemberStampResponse | null> => {
  try {
    const response = await serverApi.get<MemberStampResponse>(buildMemberStampUrl(memberUuid))
    return handleApiResponse<MemberStampResponse>(response)
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

export const uploadMemberStampImage = async (memberUuid: string, stampImageBase64: string, mimeType = "image/png"): Promise<MemberStampResponse> => {
  const response = await serverApi.post(buildMemberStampUrl(memberUuid), {
    stampImageBase64,
    mimeType,
  })
  return handleApiResponse<MemberStampResponse>(response)
}

export const deleteMemberStampImage = async (memberUuid: string): Promise<{ success: boolean }> => {
  const response = await serverApi.delete(buildMemberStampUrl(memberUuid))
  handleApiResponse(response)
  return { success: true }
}
